import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  TextField,
  List,
  ListItem,
  Paper,
  Fab,
  Badge,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { calMarkdownComponents } from './CalMarkdown';
import { useAuth0 } from '@auth0/auth0-react';
import { useCalWebSocket } from '../contexts/CalWebSocketContext';
import { useCalChat } from '../contexts/CalChatContext';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import { CalWebSocketMessage } from '../services/calWebSocketService';
import { getCalAvatar } from '../config/calAvatars';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'cal';
  timestamp: Date;
  isTyping?: boolean;
}

interface ModernCalChatProps {
  userEmail: string;
  selectedMeter?: string;
  onMeterSelect?: (meterId: string) => void;
}

const meterQuestions = [
  'What meters need attention?',
  'Show me recent anomalies',
  'Analyze meter performance',
  'What are the critical issues?',
];

// Deliberately phrased WITHOUT a dataset or period: the screen the user is on supplies those via
// CalViewContext, so a question asked while viewing August is answered about August. Naming a
// month here would override what they are actually looking at.
const massBalanceQuestions = [
  'Analyze the source of error',
  'Analyze the implied densities',
  'Show me fuel and loss trends',
  'Show me overall volumetric yield',
  'Show me mass balance closure trends',
];

const ModernCalChat: React.FC<ModernCalChatProps> = ({
  userEmail,
  selectedMeter,
  onMeterSelect,
}) => {
  const theme = useTheme();
  const { getIdTokenClaims } = useAuth0();
  const { sendMessage: sendWebSocketMessage, isConnected, addMessageHandler } = useCalWebSocket();
  const { can } = useCapabilities();

  // Use the shared Cal chat context
  const {
    messages: contextMessages,
    sessionId,
    viewContext,
    unreadCount,
    isDrawerOpen,
    sendMessage: sendContextMessage,
    addMessage,
    updateMessage,
    clearUnreadCount,
    toggleDrawer,
    setActiveView,
    clearMessages,
    activeView
  } = useCalChat();

  // In Mass Balance -> show only its prompts; anywhere else (MFCC, dashboard) -> meter prompts.
  // Still gated on the capability: someone without use_mass_balance cannot reach that screen, so
  // they only ever see the meter set.
  const inMassBalance = Boolean(viewContext?.massBalance) && can('use_mass_balance');

  const [open, setOpen] = useState(false);
  // User-resizable panel width (desktop), remembered across sessions via localStorage.
  const CHAT_MIN_W = 360;
  const chatMaxW = () => Math.round((typeof window !== 'undefined' ? window.innerWidth : 1200) * 0.95);
  // Default: a share of the viewport rather than a fixed 420px. Cal's answers are frequently
  // markdown TABLES (KPI summaries, grade breakdowns) and 420px wrapped them into unreadability.
  // 40% of the viewport. Cal's answers are frequently markdown TABLES (KPI summaries, grade
  // breakdowns) and the previous fixed 420px wrapped them into unreadability. Clamped only by the
  // existing min/max so it stays 40% on a large monitor rather than hitting an arbitrary cap.
  const chatDefaultW = () => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
    return Math.min(Math.max(Math.round(vw * 0.40), CHAT_MIN_W), chatMaxW());
  };
  // NOTE the storage key is versioned. The previous build persisted the width on first render,
  // not just on resize, so every existing user already has 420 saved whether or not they ever
  // touched the handle — a plain default change would have reached nobody. A new key lets the
  // better default apply once, while anyone who deliberately resizes still gets it remembered.
  const CHAT_W_KEY = 'calChatWidth.v2';
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    try {
      const s = parseInt(localStorage.getItem(CHAT_W_KEY) || '', 10);
      if (!isNaN(s)) return Math.min(Math.max(s, CHAT_MIN_W), chatMaxW());
    } catch { /* ignore */ }
    return chatDefaultW();
  });
  // Persist only on a real resize (see startResize), never on mount — otherwise the default
  // immediately becomes a "preference" and can never be improved again.
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const onMove = (ev: MouseEvent) => {
      // Drawer is anchored right, so width = viewport width minus the cursor's X.
      const w = Math.min(Math.max(window.innerWidth - ev.clientX, CHAT_MIN_W), chatMaxW());
      setPanelWidth(w);
      try { localStorage.setItem(CHAT_W_KEY, String(w)); } catch { /* ignore */ }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentChunk, setCurrentChunk] = useState('');
  const currentChunkRef = useRef<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Convert context messages to the local Message format
  const messages: Message[] = contextMessages.map(msg => ({
    id: msg.id,
    text: msg.text,
    sender: msg.sender,
    timestamp: msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp),
    isTyping: msg.text === '...' && msg.sender === 'cal'
  }));

  // Add welcome message if no messages exist
  useEffect(() => {
    if (contextMessages.length === 0) {
      addMessage({
        text: "Hi! I'm Cal, your AI Loss Control Partner. I can help you analyze meter performance, identify risks, and answer questions about your proving reports. How can I assist you today?",
        sender: 'cal',
        timestamp: new Date()
      });
    }
  }, []);

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle drawer open/close and active view tracking
  useEffect(() => {
    if (open) {
      setActiveView('floating');
      clearUnreadCount();
    } else {
      setActiveView('none');
    }
  }, [open, setActiveView, clearUnreadCount]);

  // Handle WebSocket messages
  useEffect(() => {
    const handleWebSocketMessage = (data: CalWebSocketMessage) => {
      // Only handle messages when this chat is active
      if (activeView !== 'floating') {
        return;
      }

      switch (data.type) {
        case 'status':
          // Cal is thinking
          setIsLoading(true);
          break;

        case 'chunk':
          // Append chunk to current message
          const newChunk = currentChunkRef.current + (data.content || '');
          currentChunkRef.current = newChunk;
          setCurrentChunk(newChunk);
          break;

        case 'complete':
          // Add complete message to shared context
          // Check for both message and response fields like CalComplianceTab does
          const completeMessage = data.message || (data as any).response || currentChunkRef.current;

          addMessage({
            text: completeMessage || 'I apologize, but I encountered an issue processing your request.',
            sender: 'cal',
            timestamp: new Date(),
          });
          setCurrentChunk('');
          currentChunkRef.current = '';
          setIsLoading(false);
          break;

        case 'error':
          // Handle error - add to shared context
          addMessage({
            text: data.message || (data as any).error || 'I apologize, but I encountered an error. Please try again.',
            sender: 'cal',
            timestamp: new Date(),
          });
          setIsLoading(false);
          break;
      }
    };

    const unsubscribe = addMessageHandler(handleWebSocketMessage);
    return unsubscribe;
  }, [addMessageHandler, addMessage, activeView]);


  const handleSendMessage = async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText) return;

    // Add user message to shared context
    addMessage({
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
    });

    setInput('');
    setIsLoading(true);

    try {
      if (isConnected) {
        // Use WebSocket - NO TIMEOUT!
        // Send with proper context structure matching CalComplianceTab
        const context = {
          sessionId,
          userName: userEmail?.split('@')[0] || 'User',
          userEmail: userEmail,
          activeView: 'floating',
          // What the user is currently viewing (e.g. Mass Balance dataset + period), so a
          // question asked while looking at August is answered about August.
          ...viewContext
        };
        sendWebSocketMessage(messageText, context);
        // Response will be handled by the WebSocket message handler
      } else {
        // Fallback to REST API
        console.log('WebSocket not connected, falling back to REST');
        const tokenClaims = await getIdTokenClaims();
        const idToken = tokenClaims?.__raw;

        const response = await fetch(`${API_ENDPOINT}/cal/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            message: messageText,
            sessionId,
            userContext: {
              email: userEmail,
            },
          }),
        });

        if (!response.ok) throw new Error('Failed to get response');

        const data = await response.json();

        // Add Cal's response to shared context
        addMessage({
          text: data.response || 'I apologize, but I encountered an issue processing your request.',
          sender: 'cal',
          timestamp: new Date(),
        });

        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message to shared context
      addMessage({
        text: 'I apologize, but I encountered an error. Please try again later.',
        sender: 'cal',
        timestamp: new Date(),
      });
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    clearUnreadCount();
  };

  return (
    <>
      {/* Modern Floating Action Button */}
      <Fab
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          boxShadow: theme.shadows[4],
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: theme.shadows[5],
          },
          transition: 'all 0.3s ease',
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <ChatIcon />
        </Badge>
      </Fab>

      {/* Modern Chat Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: panelWidth },
            maxWidth: '100vw',
            background: theme.palette.background.default,
            overflow: 'visible',
          },
        }}
      >
        {/* Drag handle to resize the panel (desktop only); width is remembered. */}
        <Box
          onMouseDown={startResize}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '7px',
            cursor: 'col-resize',
            zIndex: (t) => t.zIndex.drawer + 2,
            display: { xs: 'none', sm: 'block' },
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.35) },
            '&:active': { backgroundColor: alpha(theme.palette.primary.main, 0.55) },
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Box
                  component="img"
                  src={getCalAvatar().path}
                  alt="Cal"
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid rgba(255,255,255,0.6)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  }}
                />
                <Box sx={{
                  position: 'absolute', bottom: 1, right: 1,
                  width: 11, height: 11, borderRadius: '50%',
                  backgroundColor: '#22c55e', border: '2px solid #fff',
                }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
                  Cal
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', display: 'inline-block' }} />
                  AI Loss Control Partner
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => clearMessages()}
                sx={{ color: 'white' }}
                title="Clear Chat"
              >
                <DeleteIcon />
              </IconButton>
              <IconButton onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Quick Actions */}
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            {/* Quick questions follow the screen. viewContext.massBalance is published only by the
                VISIBLE Mass Balance tab and cleared when the user leaves, so its presence is a
                reliable "is the user in Mass Balance right now" signal. Showing meter prompts there
                (or vice versa) just invites questions about data that is not on screen. */}
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {inMassBalance ? 'MASS BALANCE QUICK QUESTIONS' : 'METER QUICK QUESTIONS'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {(inMassBalance ? massBalanceQuestions : meterQuestions).map((question) => (
                <Chip
                  key={question}
                  label={question}
                  size="small"
                  onClick={() => handleSendMessage(question)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                />
              ))}
            </Box>

          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <List>
              <AnimatePresence>
                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <ListItem
                      sx={{
                        flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                        gap: 1,
                        p: 1,
                      }}
                    >
                      {message.sender === 'cal' && (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            flexShrink: 0,
                            alignSelf: 'flex-end',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                          }}
                        >
                          <img
                            src={getCalAvatar().path}
                            alt="Cal"
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: 'cover',
                              borderRadius: '50%'
                            }}
                          />
                        </Box>
                      )}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          px: 1.75,
                          // Cal messages hold tables -> give them the room; user messages stay compact.
                          maxWidth: message.sender === 'cal' ? '96%' : '82%',
                          minWidth: 0,
                          overflowX: 'auto',
                          color: message.sender === 'user' ? '#fff' : theme.palette.text.primary,
                          background: message.sender === 'user'
                            ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                            : theme.palette.background.paper,
                          border: message.sender === 'user' ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                          borderRadius: 2.5,
                          borderBottomRightRadius: message.sender === 'user' ? '4px' : undefined,
                          borderBottomLeftRadius: message.sender === 'cal' ? '4px' : undefined,
                          boxShadow: message.sender === 'user'
                            ? '0 2px 10px rgba(0,0,0,0.14)'
                            : '0 1px 5px rgba(0,0,0,0.06)',
                        }}
                      >
                        {message.sender === 'cal' ? (
                          <Box sx={{
                            '& p': { m: 0 },
                            '& ul, & ol': { m: 0, pl: 2 },
                            '& table': {
                              borderCollapse: 'collapse',
                              width: '100%',
                              mt: 1,
                              mb: 1,
                            },
                            '& th, & td': {
                              border: `1px solid ${theme.palette.divider}`,
                              p: 0.5,
                              fontSize: '0.875rem',
                            },
                            '& th': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              fontWeight: 600,
                            },
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={calMarkdownComponents}>{message.text}</ReactMarkdown>
                          </Box>
                        ) : (
                          <Typography variant="body2">{message.text}</Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: message.sender === 'user' ? 'rgba(255,255,255,0.75)' : theme.palette.text.secondary,
                            display: 'block',
                            textAlign: message.sender === 'user' ? 'right' : 'left',
                            mt: 0.5,
                            fontSize: '0.68rem',
                          }}
                        >
                          {message.timestamp.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Paper>
                    </ListItem>
                  </motion.div>
                ))}
              </AnimatePresence>
              {isLoading && (
                <ListItem sx={{ gap: 1, alignItems: 'flex-end' }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      flexShrink: 0,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    }}
                  >
                    <img
                      src={getCalAvatar().path}
                      alt="Cal"
                      style={{
                        width: 32,
                        height: 32,
                        objectFit: 'cover',
                        borderRadius: '50%',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }}
                    />
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.75,
                      py: 1.25,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 2.5,
                      borderBottomLeftRadius: '4px',
                      boxShadow: '0 1px 5px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Box sx={{
                      display: 'flex', gap: 0.6, alignItems: 'center',
                      '& span': {
                        width: 7, height: 7, borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.primary.main, 0.6),
                        animation: 'calTyping 1.2s ease-in-out infinite',
                      },
                      '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                      '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                      '@keyframes calTyping': {
                        '0%, 60%, 100%': { opacity: 0.3, transform: 'translateY(0)' },
                        '30%': { opacity: 1, transform: 'translateY(-3px)' },
                      },
                    }}>
                      <span /><span /><span />
                    </Box>
                  </Paper>
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Input */}
          <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`, backgroundColor: theme.palette.background.paper }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                placeholder="Ask Cal about your meters..."
                variant="outlined"
                size="small"
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    // Always the surface colour (white in light mode) -- do NOT tint with
                    // action.hover; alpha() on it resolves to a dark rgba(0,0,0,0.x).
                    backgroundColor: theme.palette.background.paper,
                    transition: 'border-color 0.2s',
                    '& fieldset': { borderColor: alpha(theme.palette.divider, 0.8) },
                    '&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.4) },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: '1.5px' },
                  },
                }}
              />
              <IconButton
                onClick={() => handleSendMessage()}
                disabled={!input.trim() || isLoading}
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  color: 'white',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                  transition: 'transform 0.15s',
                  '&:hover': { transform: 'scale(1.06)', background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` },
                  '&.Mui-disabled': { background: theme.palette.action.disabledBackground, color: theme.palette.action.disabled },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
            {selectedMeter && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  color: theme.palette.text.secondary,
                }}
              >
                Context: Meter {selectedMeter}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}
            >
              Cal can make mistakes. Check important info.
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default ModernCalChat;